import React from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

// Zajednicki stil za react-select da se uklopi sa ostatkom aplikacije
export const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '42px',
        borderRadius: '0.25rem',
        borderColor: state.isFocused ? 'rgb(29 196 233)' : 'rgb(229 231 235)',
        boxShadow: 'none',
        '&:hover': { borderColor: 'rgb(29 196 233)' },
        fontSize: '0.875rem',
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: 'rgba(29, 196, 233, 0.12)',
        borderRadius: '9999px',
        paddingLeft: '4px',
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: 'rgb(63 77 103)',
        fontWeight: 500,
        whiteSpace: 'normal',   // dugacki ciljevi se prelamaju umesto da iscure iz polja
    }),
    multiValueRemove: (base) => ({
        ...base,
        borderRadius: '9999px',
        ':hover': { backgroundColor: 'rgba(29, 196, 233, 0.35)', color: 'rgb(63 77 103)' },
    }),
    menu: (base) => ({ ...base, zIndex: 30, fontSize: '0.875rem' }),
    placeholder: (base) => ({ ...base, color: 'rgb(156 163 175)' }),
};

const Label = ({ children }) => (
    <label className="font-medium text-xs text-gray-500 mb-1 block">{children}</label>
);

// Sivi prikaz starog slobodnog unosa ispod novog polja
export const OldEntry = ({ value }) => {
    if (!value || !String(value).trim()) return null;
    return (
        <div className="mt-1 text-xs text-gray-400 italic break-words">
            Stari upis: {value}
        </div>
    );
};

// Obicno tekstualno polje
export const TextField = ({ label, value, onChange, type = 'text', placeholder }) => (
    <div>
        <Label>{label}</Label>
        <input
            type={type}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            className="input-field"
        />
    </div>
);

// Veliko tekstualno polje
export const TextAreaField = ({ label, value, onChange, rows = 3 }) => (
    <div>
        <Label>{label}</Label>
        <textarea
            value={value}
            rows={rows}
            onChange={(e) => onChange(e.target.value)}
            className="input-field"
        ></textarea>
    </div>
);

// Da / Ne prekidac. Vrednost je tekst 'Da' ili 'Ne' (prazno = nije uneto).
// Ako je u bazi ostao neki stariji proizvoljan tekst, prikazuje se ispod kao stari upis.
export const YesNoField = ({ label, value, onChange }) => {
    const normalized = String(value || '').trim().toLowerCase();
    const isYes = normalized === 'da';
    const isNo = normalized === 'ne';
    const isLegacy = !!normalized && !isYes && !isNo;

    const buttonClass = (active) =>
        'px-4 py-2 text-sm font-medium transition border first:rounded-l last:rounded-r focus:outline-none ' +
        (active
            ? 'bg-bluePrimary text-white border-bluePrimary'
            : 'bg-white text-gray-600 border-gray-200 hover:border-bluePrimary');

    return (
        <div>
            <Label>{label}</Label>
            <div className="flex">
                <button type="button" className={buttonClass(isYes)} onClick={() => onChange(isYes ? '' : 'Da')}>Da</button>
                <button type="button" className={buttonClass(isNo)} onClick={() => onChange(isNo ? '' : 'Ne')}>Ne</button>
            </div>
            {isLegacy && <OldEntry value={value} />}
        </div>
    );
};

// Jedan izbor iz fiksne liste. Ako je u bazi vrednost koje nema na listi,
// dodaje se kao opcija da se ne izgubi iz prikaza.
export const SingleSelectField = ({ label, value, onChange, options }) => {
    const baseOptions = options.map((o) => ({ value: o, label: o }));
    const hasValue = !!value && !options.includes(value);
    const allOptions = hasValue ? [{ value, label: value + ' (stari upis)' }, ...baseOptions] : baseOptions;
    const selected = value ? allOptions.find((o) => o.value === value) : null;
    return (
        <div>
            <Label>{label}</Label>
            <Select
                styles={selectStyles}
                options={allOptions}
                value={selected}
                isClearable
                placeholder="Izaberi..."
                noOptionsMessage={() => 'Nema opcija'}
                onChange={(opt) => onChange(opt ? opt.value : '')}
            />
        </div>
    );
};

// Visestruki izbor iz sifarnika sa dodavanjem u hodu.
// value je niz tekstova; tagOptions je niz naziva iz sifarnika; onCreate cuva novu stavku.
export const TagsField = ({ label, value, onChange, tagOptions, onCreate, oldValue, placeholder, manageItems, onRename, onDelete }) => {
    const options = (tagOptions || []).map((name) => ({ value: name, label: name }));
    const selected = (value || []).map((name) => ({ value: name, label: name }));
    const canManage = typeof onRename === 'function' && typeof onDelete === 'function';

    // ID stavke sifarnika po imenu (za izmenu/brisanje iz dropdown-a)
    const idByName = {};
    (manageItems || []).forEach((it) => { idByName[it.name] = it.id; });

    // Stavka koja se trenutno menja - prikazuje se ISPOD polja (ne u meniju),
    // da ne bi doslo do sukoba sa fokusom react-select-a (koji inace zatvori meni).
    const [editing, setEditing] = React.useState(null); // { id, name }
    const [editText, setEditText] = React.useState('');

    const startEdit = (id, name) => { setEditing({ id, name }); setEditText(name); };
    const saveEdit = () => {
        if (editing) onRename(editing.id, editing.name, editText);
        setEditing(null);
    };

    // Custom prikaz stavke u meniju: naziv levo, olovka i kanta desno
    const Option = (props) => {
        const { data, innerRef, innerProps, isFocused } = props;
        const id = idByName[data.value];
        return (
            <div ref={innerRef} {...innerProps}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: isFocused ? '#f0f0f0' : '#fff', cursor: 'pointer' }}>
                <span style={{ fontSize: '13px', color: '#333' }}>{data.label}</span>
                {canManage && id &&
                    <span style={{ display: 'flex', gap: '12px' }}>
                        <span title="Izmeni" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); startEdit(id, data.value); }}
                            style={{ cursor: 'pointer', color: '#37a', fontSize: '14px' }}>&#9998;</span>
                        <span title="Obriši" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (window.confirm('Obrisati "' + data.value + '" iz menija?')) onDelete(id); }}
                            style={{ cursor: 'pointer', color: '#c44', fontSize: '14px' }}>&#128465;</span>
                    </span>}
            </div>
        );
    };

    return (
        <div>
            <Label>{label}</Label>
            <CreatableSelect
                isMulti
                styles={selectStyles}
                options={options}
                value={selected}
                placeholder={placeholder || 'Izaberi ili ukucaj...'}
                noOptionsMessage={() => 'Kucaj da dodaš novu stavku'}
                formatCreateLabel={(input) => 'Dodaj: "' + input + '"'}
                components={canManage ? { Option } : undefined}
                closeMenuOnSelect={false}
                onChange={(selectedOptions) => onChange((selectedOptions || []).map((o) => o.value))}
                onCreateOption={(input) => {
                    const name = String(input || '').trim();
                    if (!name) return;
                    onChange([...(value || []), name]);
                    if (onCreate) onCreate(name);
                }}
            />
            {editing &&
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', padding: '6px 8px', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                    <span style={{ fontSize: '12px', color: '#777' }}>Izmena:</span>
                    <input value={editText} autoFocus onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }}
                        style={{ flex: 1, fontSize: '13px', padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                    <button type="button" onClick={saveEdit}
                        style={{ fontSize: '12px', color: '#fff', background: '#2a9d6e', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer' }}>Sačuvaj</button>
                    <button type="button" onClick={() => setEditing(null)}
                        style={{ fontSize: '12px', color: '#666', background: 'none', border: 'none', cursor: 'pointer' }}>otkaži</button>
                </div>}
            <OldEntry value={oldValue} />
        </div>
    );
};

// Pretvara ono sto je zapisano u polju u format koji birac datuma razume (GGGG-MM-DD).
// Prihvata: 1985-03-15, 1985-03-15T00:00:00.000Z, 15.03.1985., 15/3/1985
// Ako ne prepozna format, vraca prazno - tada se stari upis prikaze ispod polja.
export const toIsoDate = (value) => {
    if (!value) return '';
    const text = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

    const isoWithTime = text.match(/^(\d{4}-\d{2}-\d{2})T/);
    if (isoWithTime) return isoWithTime[1];

    const parts = text.match(/^(\d{1,2})[.\/-](\d{1,2})[.\/-](\d{4})\.?$/);
    if (parts) {
        const day = Number(parts[1]);
        const month = Number(parts[2]);
        const year = Number(parts[3]);
        const check = new Date(year, month - 1, day);
        if (check.getFullYear() === year && check.getMonth() === month - 1 && check.getDate() === day) {
            const pad = (n) => (n < 10 ? '0' + n : String(n));
            return year + '-' + pad(month) + '-' + pad(day);
        }
    }
    return '';
};

// Prikaz datuma u nasem obliku: 1985-03-15 -> 15.03.1985.
export const formatDate = (value) => {
    const iso = toIsoDate(value);
    if (!iso) return value || '';
    const [year, month, day] = iso.split('-');
    return day + '.' + month + '.' + year + '.';
};

// Birac datuma preko tri padajuca menija: dan / mesec / godina.
// Izgleda isto na svakom racunaru, bez obzira na podesavanja browsera.
// Vrednost se cuva kao GGGG-MM-DD, pa se kasnije moze filtrirati po godini
// rodjenja, opsegu datuma i slicno.
const MONTH_NAMES = ['januar', 'februar', 'mart', 'april', 'maj', 'jun',
    'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar'];

// Koliko dana ima mesec (uz prestupnu godinu)
const daysInMonth = (month, year) => {
    if (!month) return 31;
    const y = year || 2000;
    return new Date(y, month, 0).getDate();
};

export const DateField = ({ label, value, onChange }) => {
    const iso = toIsoDate(value);
    const hasUnrecognized = !iso && !!value && String(value).trim() !== '';

    // Sva tri izbora drzimo u lokalnom stanju. Bez toga bi delimican izbor
    // (npr. samo dan) odmah bio obrisan jer vrednost jos nije kompletan datum,
    // pa se tri menija nikad ne bi skupila u datum.
    const fromIso = (isoStr) => {
        if (!isoStr) return { day: '', month: '', year: '' };
        const p = isoStr.split('-');
        return { year: Number(p[0]), month: Number(p[1]), day: Number(p[2]) };
    };
    const [parts, setParts] = React.useState(() => fromIso(iso));

    // Kad datum stigne spolja (drugi klijent), osvezavamo lokalno stanje.
    const lastIsoRef = React.useRef(iso);
    React.useEffect(() => {
        if (iso !== lastIsoRef.current) {
            lastIsoRef.current = iso;
            setParts(fromIso(iso));
        }
    }, [iso]);

    const { day, month, year } = parts;

    // Sastavlja datum iz tri izbora i pamti ga lokalno.
    // Dok nisu sva tri popunjena, spolja saljemo prazno (datum nije kompletan).
    const apply = (next) => {
        setParts(next);
        if (next.day && next.month && next.year) {
            const pad = (n) => (n < 10 ? '0' + n : String(n));
            const built = next.year + '-' + pad(next.month) + '-' + pad(next.day);
            lastIsoRef.current = built;
            onChange(built);
        } else {
            lastIsoRef.current = '';
            onChange('');
        }
    };

    const onDay = (e) => apply({ ...parts, day: Number(e.target.value) || '' });
    const onMonth = (e) => {
        const m = Number(e.target.value) || '';
        let d = day;
        if (d && m && d > daysInMonth(m, year)) d = daysInMonth(m, year);
        apply({ ...parts, month: m, day: d });
    };
    const onYear = (e) => {
        const y = Number(e.target.value) || '';
        let d = day;
        if (d && month && d > daysInMonth(month, y)) d = daysInMonth(month, y);
        apply({ ...parts, year: y, day: d });
    };

    const maxDay = daysInMonth(month, year);
    const days = [];
    for (let i = 1; i <= maxDay; i++) days.push(i);

    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= 1920; y--) years.push(y);

    return (
        <div>
            <Label>{label}</Label>
            <div className="flex gap-2">
                <select value={day} onChange={onDay} className="input-field" style={{ minWidth: 0 }} aria-label="Dan">
                    <option value="">Dan</option>
                    {days.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <select value={month} onChange={onMonth} className="input-field" style={{ minWidth: 0 }} aria-label="Mesec">
                    <option value="">Mesec</option>
                    {MONTH_NAMES.map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
                </select>
                <select value={year} onChange={onYear} className="input-field" style={{ minWidth: 0 }} aria-label="Godina">
                    <option value="">Godina</option>
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
            </div>
            {hasUnrecognized && <OldEntry value={value} />}
        </div>
    );
};

// Naslov sekcije kartona
export const SectionTitle = ({ children }) => (
    <div className="w-full mt-8 mb-3 first:mt-2">
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b border-gray-200 pb-2">
            {children}
        </h4>
    </div>
);
